import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axios, useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';

const MOTHER_TONGUES = ['Telugu','Hindi','Urdu','Tamil','Kannada','Marathi','Bengali','Punjabi','Other'];
const STATUSES = ['None','Available','Shifted','Expired'];

export default function VoterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isUser } = useAuth();
  const [voter, setVoter]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [showMobile, setShowMobile] = useState(false);
  const [form, setForm]             = useState({});
  const [gettingLoc, setGettingLoc] = useState(false);
  const [family, setFamily]         = useState([]);
  const [showFamily, setShowFamily] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`/voters/${id}`);
        if (!cancelled) {
          setVoter(data);
          setForm({
            // Personal
            name:           data.name           || '',
            relation_name:  data.relation_name  || '',
            relation_type:  data.relation_type  || 'S/o',
            age:            data.age            || '',
            gender:         data.gender         || '',
            // Contact
            mobile:         data.mobile         || '',
            voter_id:       data.voter_id       || '',
            // Address
            house_number:   data.house_number   || '',
            address:        data.address        || '',
            district:       data.district       || '',
            area:           data.area           || '',
            // Additional
            status:         data.status         || 'None',
            community:      data.community      || '',
            profession:     data.profession     || '',
            degree:         data.degree         || '',
            mother_tongue:  data.mother_tongue  || '',
            reference_name: data.reference_name || '',
            team_status:    data.team_status    || '',
            latitude:       data.latitude       || '',
            longitude:      data.longitude      || '',
          });
        }
      } catch { if (!cancelled) toast.error('Failed to load voter'); }
      finally   { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  const [showConfirm, setShowConfirm] = useState(false);

  const handleUpdateClick = () => {
    setShowConfirm(true);
  };

  const handleUpdate = async () => {
    setShowConfirm(false);
    setSaving(true);
    try {
      const { data } = await axios.put(`/voters/${id}`, form);
      setVoter(data);
      setForm(f => ({
        ...f,
        mobile:         data.mobile         || f.mobile,
        status:         data.status         || f.status,
        profession:     data.profession     || f.profession,
        community:      data.community      || f.community,
        mother_tongue:  data.mother_tongue  || f.mother_tongue,
        reference_name: data.reference_name || f.reference_name,
        team_status:    data.team_status    || f.team_status,
        latitude:       data.latitude       || f.latitude,
        longitude:      data.longitude      || f.longitude,
      }));
      toast.success('✅ Updated successfully!');
    } catch(err) { toast.error(err.response?.data?.error || 'Update failed'); }
    finally { setSaving(false); }
  };

  const handleReset = () => {
    if (!voter) return;
    setForm({
      name:           voter.name           || '',
      relation_name:  voter.relation_name  || '',
      relation_type:  voter.relation_type  || 'S/o',
      age:            voter.age            || '',
      gender:         voter.gender         || '',
      mobile:         voter.mobile         || '',
      voter_id:       voter.voter_id       || '',
      house_number:   voter.house_number   || '',
      address:        voter.address        || '',
      district:       voter.district       || '',
      area:           voter.area           || '',
      status:         voter.status         || 'None',
      community:      voter.community      || '',
      profession:     voter.profession     || '',
      degree:         voter.degree         || '',
      mother_tongue:  voter.mother_tongue  || '',
      reference_name: voter.reference_name || '',
      team_status:    voter.team_status    || '',
      latitude:       voter.latitude       || '',
      longitude:      voter.longitude      || '',
    });
    toast('Reset to original values', { icon: '↺' });
  };

  const getLocation = () => {
    if (!navigator.geolocation) { toast.error('GPS not available'); return; }
    setGettingLoc(true);
    navigator.geolocation.getCurrentPosition(
      p => { setForm(f=>({...f, latitude:p.coords.latitude, longitude:p.coords.longitude})); toast.success('📍 Captured!'); setGettingLoc(false); },
      () => { toast.error('Location denied'); setGettingLoc(false); }
    );
  };

  const handleCall  = () => voter?.mobile ? window.open(`tel:${voter.mobile}`) : toast.error('No mobile');
  const handleSMS   = () => voter?.mobile ? window.open(`sms:${voter.mobile}`) : toast.error('No mobile');
  const handleWA    = () => { const msg = encodeURIComponent(`Hello ${voter?.name || ''}`); window.open(`https://wa.me/91${voter?.mobile}?text=${msg}`); };
  const fetchFamily = async () => {
    try { const {data} = await axios.get(`/voters/family/${id}`); setFamily(data); setShowFamily(true); }
    catch { toast.error('Failed to load family'); }
  };

  const handlePrint = () => {
    if (!voter) return;
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Voter - ${voter.name}</title>
        <style>
          * { margin:0; padding:0; box-sizing:border-box; }
          body { font-family: Arial, sans-serif; background: white; }
          .header { background: #E55100; color: white; padding: 14px 20px; display: flex; align-items: center; justify-content: space-between; }
          .header img { width: 50px; height: 50px; border-radius: 50%; object-fit: cover; }
          .header-center { text-align: center; flex: 1; }
          .header-center h1 { font-size: 18px; font-weight: bold; }
          .header-center p { font-size: 11px; opacity: 0.9; margin-top: 2px; }
          .green-strip { background: #138808; height: 6px; }
          .id-badge { background: #FFF8E1; border: 1.5px solid #E55100; margin: 12px 16px; padding: 8px 14px; border-radius: 8px; display: flex; justify-content: space-between; align-items: center; }
          .id-badge span { font-weight: bold; color: #E55100; font-size: 13px; }
          .id-badge small { color: #666; font-size: 11px; }
          .section { margin: 0 16px 12px; }
          .section-title { background: #FFD6D6; color: #8B0000; font-weight: bold; font-size: 12px; padding: 5px 10px; border-radius: 6px 6px 0 0; }
          .blue .section-title { background: #D6E8FF; color: #003080; }
          .grey .section-title { background: #E8E8E8; color: #333; }
          .section-body { border: 1px solid #ddd; border-top: none; border-radius: 0 0 6px 6px; padding: 10px 14px; }
          .row { display: flex; margin-bottom: 5px; font-size: 12px; }
          .label { color: #666; width: 110px; flex-shrink: 0; }
          .value { font-weight: bold; color: #111; }
          .footer { background: #E55100; color: white; text-align: center; padding: 8px; font-size: 10px; margin-top: 12px; }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            @page { margin: 0.5cm; size: A5; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="${window.location.origin}/images/modi.png" onerror="this.style.display='none'" alt="Modi"/>
          <div class="header-center">
            <h1>Bharatiya Janata Party</h1>
            <p>Voter Data Management System | Influfly</p>
          </div>
          <img src="${window.location.origin}/images/bjp-logo.png" onerror="this.style.display='none'" alt="BJP"/>
        </div>
        <div class="green-strip"></div>
        <div class="id-badge">
          <span>UU ID: ${voter.epic_id||'N/A'}</span>
          <small>Serial No: ${voter.serial_number||'N/A'} &nbsp;|&nbsp; Booth: ${voter.booth_number||'N/A'}</small>
        </div>
        <div class="section">
          <div class="section-title">VOTER'S INFORMATION</div>
          <div class="section-body">
            <div class="row"><span class="label">Name:</span><span class="value">${voter.name||'N/A'}</span></div>
            <div class="row"><span class="label">Gender / Age:</span><span class="value">${voter.gender==='M'?'Male':voter.gender==='F'?'Female':'Other'} / ${voter.age||'N/A'} yrs</span></div>
            <div class="row"><span class="label">Relative:</span><span class="value">${voter.relation_name||'N/A'}</span></div>
            <div class="row"><span class="label">House No:</span><span class="value">${voter.house_number||'N/A'}</span></div>
            <div class="row"><span class="label">Address:</span><span class="value">${voter.address||'N/A'}</span></div>
            ${voter.voter_id?`<div class="row"><span class="label">Voter ID:</span><span class="value">${voter.voter_id}</span></div>`:''}
          </div>
        </div>
        <div class="section blue">
          <div class="section-title">VOTING INFORMATION</div>
          <div class="section-body">
            <div class="row"><span class="label">District:</span><span class="value">${voter.district||'N/A'}</span></div>
            <div class="row"><span class="label">UU ID:</span><span class="value">${voter.epic_id||'N/A'}</span></div>
            <div class="row"><span class="label">Booth No:</span><span class="value">${voter.booth_number||'N/A'}</span></div>
            <div class="row"><span class="label">Serial No:</span><span class="value">${voter.serial_number||'N/A'}</span></div>
            <div class="row"><span class="label">Area:</span><span class="value">${voter.area||'N/A'}</span></div>
          </div>
        </div>
        <div class="section grey">
          <div class="section-title">ADDITIONAL INFORMATION</div>
          <div class="section-body">
            <div class="row"><span class="label">Status:</span><span class="value">${voter.status||'None'}</span></div>
            <div class="row"><span class="label">Community:</span><span class="value">${voter.community||'N/A'}</span></div>
            <div class="row"><span class="label">Profession:</span><span class="value">${voter.profession||'N/A'}</span></div>
            <div class="row"><span class="label">Mother Tongue:</span><span class="value">${voter.mother_tongue||'N/A'}</span></div>
            <div class="row"><span class="label">Reference:</span><span class="value">${voter.reference_name||'N/A'}</span></div>
          </div>
        </div>
        <div class="footer">Printed on ${new Date().toLocaleString('en-IN', { timeZone:'Asia/Kolkata' })} | Voter App by Influfly | Confidential</div>
        <script>
          window.onload = function() {
            setTimeout(function() { window.print(); }, 500);
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handlePDFDownload = async () => {
    if (!voter) return;
    const doc = new jsPDF('p','mm','a4');
    const W = 210;
    doc.setFillColor(229,81,0); doc.rect(0,0,W,38,'F');
    doc.setFillColor(19,136,8); doc.rect(0,32,W,6,'F');
    // Helper to make circular image
    const makeCircle = (img) => {
      const size = Math.min(img.naturalWidth, img.naturalHeight);
      const c = document.createElement('canvas');
      c.width = size; c.height = size;
      const ctx = c.getContext('2d');
      ctx.beginPath();
      ctx.arc(size/2, size/2, size/2, 0, Math.PI*2);
      ctx.closePath();
      ctx.clip();
      const ox = (img.naturalWidth - size) / 2;
      const oy = (img.naturalHeight - size) / 2;
      ctx.drawImage(img, -ox, -oy);
      return c.toDataURL('image/png');
    };

    // Modi on LEFT — circle
    try {
      const modiRes = await fetch('/images/modi.png');
      const modiBlob = await modiRes.blob();
      const modiData = await new Promise(r => { const fr=new FileReader(); fr.onload=e=>r(e.target.result); fr.readAsDataURL(modiBlob); });
      const modiImg = new Image();
      await new Promise(r => { modiImg.onload=r; modiImg.src=modiData; });
      doc.addImage(makeCircle(modiImg), 'PNG', 3, 3, 28, 28);
    } catch(e){ console.log('Modi error:', e); }

    // BJP Logo on RIGHT — circle
    try {
      const bjpRes = await fetch('/images/bjp-logo.png');
      const bjpBlob = await bjpRes.blob();
      const bjpData = await new Promise(r => { const fr=new FileReader(); fr.onload=e=>r(e.target.result); fr.readAsDataURL(bjpBlob); });
      const bjpImg = new Image();
      await new Promise(r => { bjpImg.onload=r; bjpImg.src=bjpData; });
      doc.addImage(makeCircle(bjpImg), 'PNG', 179, 3, 28, 28);
    } catch(e){ console.log('BJP error:', e); }
    doc.setTextColor(255,255,255); doc.setFontSize(15); doc.setFont(undefined,'bold');
    doc.text('Bharatiya Janata Party', W/2, 14, {align:'center'});
    doc.setFontSize(8); doc.setFont(undefined,'normal');
    doc.text('Voter Data Management System | Influfly', W/2, 22, {align:'center'});
    doc.setFillColor(255,248,220); doc.roundedRect(10,42,W-20,14,3,3,'F');
    doc.setDrawColor(229,81,0); doc.setLineWidth(0.5); doc.roundedRect(10,42,W-20,14,3,3,'S');
    doc.setTextColor(229,81,0); doc.setFontSize(10); doc.setFont(undefined,'bold');
    doc.text(`UU ID: ${voter.epic_id||'N/A'}`,18,50);
    doc.setTextColor(100,100,100); doc.setFontSize(9);
    doc.text(`Serial No: ${voter.serial_number||'N/A'}`,100,50);
    doc.text(`Booth: ${voter.booth_number||'N/A'}`,160,50);
    let y=62;
    const section=(bg,tc,title)=>{ doc.setFillColor(...bg); doc.rect(10,y-5,W-20,8,'F'); doc.setTextColor(...tc); doc.setFontSize(10); doc.setFont(undefined,'bold'); doc.text(title,15,y); y+=10; };
    const row=(k,v)=>{ doc.setTextColor(80,40,0); doc.setFont(undefined,'bold'); doc.text(`${k}:`,15,y); doc.setTextColor(0,0,0); doc.setFont(undefined,'normal'); doc.text(String(v||'N/A').substring(0,65),55,y); y+=8; };
    section([255,209,220],[180,0,60],"VOTER'S INFORMATION");
    [['Name',voter.name],['Gender/Age',`${voter.gender==='M'?'Male':voter.gender==='F'?'Female':'Other'} / ${voter.age} yrs`],['Relative',voter.relation_name],['House No',voter.house_number],['Address',voter.address]].forEach(([k,v])=>row(k,v));
    y+=4; section([178,210,255],[0,50,150],'VOTING INFORMATION');
    [['UU ID',voter.epic_id],['Serial No',voter.serial_number],['Booth No',voter.booth_number],['District',voter.district],['Area',voter.area]].forEach(([k,v])=>row(k,v));
    y+=4; section([230,230,230],[50,50,50],'ADDITIONAL INFORMATION');
    [['Status',voter.status],['Community',voter.community],['Profession',voter.profession],['Education',voter.degree],['Mother Tongue',voter.mother_tongue],['Reference',voter.reference_name]].forEach(([k,v])=>row(k,v));
    doc.setFillColor(229,81,0); doc.rect(0,280,W,17,'F');
    doc.setTextColor(255,255,255); doc.setFontSize(8); doc.setFont(undefined,'normal');
    doc.text(`Printed on ${new Date().toLocaleString('en-IN', { timeZone:'Asia/Kolkata' })} | Voter App by Influfly`,W/2,290,{align:'center'});
    doc.save(`voter_${(voter.name||'voter').replace(/\s+/g,'_')}_${voter.epic_id||voter.id}.pdf`);
    toast.success('📄 PDF downloaded!');
  };

  const f = (field, val) => setForm(p => ({...p, [field]: val}));

  if (loading) return (
    <div className="loader"><div className="spinner"/><p>Loading voter details...</p></div>
  );

  if (!voter) return (
    <div className="page"><div className="empty-state">
      <div style={{fontSize:'3rem',marginBottom:12}}>😕</div>
      <h3>Voter not found</h3>
      <button className="btn btn-primary" style={{marginTop:16}} onClick={()=>navigate(-1)}>← Go Back</button>
    </div></div>
  );

  return (
    <div style={{minHeight:'100vh', background:'#FFF4ED'}}>

      {/* Header */}
      <div className="app-header">
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <button onClick={()=>navigate(-1)} className="back-btn">←</button>
          <div>
            <div style={{fontFamily:"'Baloo 2',sans-serif",fontWeight:700,fontSize:'1rem',color:'white'}}>{voter.name}</div>
            <div style={{fontSize:'0.68rem',opacity:.85,color:'white'}}>{voter.gender==='M'?'Male':voter.gender==='F'?'Female':'Other'} · {voter.age} yrs</div>
          </div>
        </div>
        {isAdmin && (
          <button onClick={handleCall} style={{background:'rgba(255,255,255,0.2)',border:'1.5px solid rgba(255,255,255,0.5)',color:'white',padding:'7px 14px',borderRadius:20,cursor:'pointer',fontWeight:600,fontSize:'0.82rem'}}>📞 Call</button>
        )}
      </div>

      <div style={{maxWidth:700,margin:'0 auto',padding:'16px 16px 80px'}}>

        {/* ── ORANGE CARD 1 — Voter Info ── */}
        <div style={{background:'linear-gradient(135deg,#E55100,#FF6B00)',borderRadius:20,padding:'20px 22px',marginBottom:14,color:'white',boxShadow:'0 8px 32px rgba(229,81,0,0.3)'}}>
          <div style={{fontWeight:800,fontSize:'1.2rem',marginBottom:4}}>{voter.name}</div>
          <div style={{opacity:.9,fontSize:'0.88rem',marginBottom:6}}>
            {voter.gender==='M'?'👨 Male':voter.gender==='F'?'👩 Female':'🧑 Other'} · {voter.age} years
          </div>
          {voter.relation_name && <div style={{opacity:.88,fontSize:'0.83rem',marginBottom:3}}>👨‍👩‍👦 {voter.relation_name}</div>}
          {voter.voter_id      && <div style={{opacity:.88,fontSize:'0.83rem',marginBottom:3}}>🪪 Voter ID: {voter.voter_id}</div>}
          {voter.house_number  && <div style={{opacity:.85,fontSize:'0.83rem',marginBottom:3}}>🏠 House No: {voter.house_number}</div>}
          {voter.address       && <div style={{opacity:.82,fontSize:'0.8rem',marginTop:6,lineHeight:1.5}}>📍 {voter.address}</div>}
        </div>

        {/* ── ORANGE CARD 2 — Voting Info ── */}
        <div style={{background:'linear-gradient(135deg,#FF6B00,#FF9933)',borderRadius:20,padding:'20px 22px',marginBottom:14,color:'white',boxShadow:'0 8px 32px rgba(255,107,0,0.3)'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px 12px',fontSize:'0.82rem',marginBottom:12}}>
            <div><span style={{opacity:.75}}>District: </span><strong>{voter.district||'—'}</strong></div>
            <div><span style={{opacity:.75}}>UU ID: </span><strong style={{fontSize:'0.78rem'}}>{voter.epic_id||'—'}</strong></div>
          </div>
          <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
            {[['BOOTH NO',voter.booth_number],['SERIAL NO',voter.serial_number],voter.area&&['AREA',voter.area]].filter(Boolean).map(([lbl,val])=>(
              <div key={lbl} style={{background:'rgba(255,255,255,0.22)',padding:'8px 14px',borderRadius:10,minWidth:70}}>
                <div style={{opacity:.8,fontSize:'0.65rem',fontWeight:700,letterSpacing:'0.5px'}}>{lbl}</div>
                <div style={{fontWeight:800,fontSize:'1rem'}}>{val||'—'}</div>
              </div>
            ))}
          </div>
          {voter.booth_address&&<div style={{marginTop:10,padding:'8px 10px',background:'rgba(255,255,255,0.15)',borderRadius:8,fontSize:'0.78rem',opacity:.9}}>🏫 {voter.booth_address}</div>}
        </div>

        {/* ── ADDITIONAL INFO ── */}
        <div className="card" style={{padding:'18px'}}>
          <div className="section-title">📝 Update Voter Details</div>

          {/* ── PERSONAL DETAILS ── */}
          <div style={{background:'linear-gradient(135deg,#FFF3E0,#FFE0B2)',borderRadius:12,padding:'10px 14px',marginBottom:14}}>
            <div style={{fontWeight:700,fontSize:'0.78rem',color:'#BF360C',marginBottom:10,textTransform:'uppercase',letterSpacing:'0.5px'}}>👤 Personal Details</div>

            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input className="form-input" value={form.name} onChange={e=>f('name',e.target.value)} placeholder="Full name"/>
            </div>

            <div className="form-group">
              <label className="form-label">Relative Name</label>
              <div style={{display:'flex',gap:8}}>
                <select className="form-select" style={{width:90,flexShrink:0}} value={form.relation_type} onChange={e=>f('relation_type',e.target.value)}>
                  {['S/o','D/o','W/o','H/o','C/o'].map(r=><option key={r} value={r}>{r}</option>)}
                </select>
                <input className="form-input" value={form.relation_name} onChange={e=>f('relation_name',e.target.value)} placeholder="Father/Husband name" style={{flex:1}}/>
              </div>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
              <div className="form-group">
                <label className="form-label">Age</label>
                <select className="form-select" value={form.age} onChange={e=>f('age',e.target.value)}>
                  <option value="">Select</option>
                  {Array.from({length:83},(_,i)=>i+18).map(a=><option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Gender</label>
                <select className="form-select" value={form.gender} onChange={e=>f('gender',e.target.value)}>
                  <option value="">Select</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                  <option value="O">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* ── CONTACT & IDs ── */}
          <div style={{background:'linear-gradient(135deg,#E3F2FD,#BBDEFB)',borderRadius:12,padding:'10px 14px',marginBottom:14}}>
            <div style={{fontWeight:700,fontSize:'0.78rem',color:'#1565C0',marginBottom:10,textTransform:'uppercase',letterSpacing:'0.5px'}}>📱 Contact & IDs</div>

            <div className="form-group">
              <label className="form-label">Mobile Number</label>
              {isUser
                ? /* Enumerator — show masked dots, cannot see or edit */
                  <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'#F5F5F5',borderRadius:12,border:'2px solid #E0E0E0'}}>
                    <span style={{fontSize:'1.2rem',letterSpacing:'4px',color:'#9E9E9E'}}>••••••••••</span>
                    <span style={{fontSize:'0.72rem',color:'#9E9E9E',marginLeft:'auto'}}>🔒 Confidential</span>
                  </div>
                : /* Admin/SuperAdmin — full access */
                  <div>
                    <div style={{display:'flex',gap:8}}>
                      <input className="form-input" type={showMobile?'tel':'password'} maxLength={10} style={{flex:1}} value={form.mobile} onChange={e=>f('mobile',e.target.value.replace(/\D/g,'').slice(0,10))} placeholder="10-digit mobile"/>
                      <button onClick={()=>setShowMobile(!showMobile)} style={{width:42,height:42,borderRadius:10,background:'#E3F2FD',border:'none',cursor:'pointer',fontSize:'1.1rem',flexShrink:0}}>{showMobile?'🙈':'👁️'}</button>
                    </div>
                    {form.mobile&&<div style={{fontSize:'0.72rem',marginTop:3,color:form.mobile.length===10?'#2E7D32':'#F57F17',fontWeight:600}}>{form.mobile.length}/10 digits</div>}
                  </div>
              }
            </div>

            <div className="form-group">
              <label className="form-label">Voter ID</label>
              <input className="form-input" value={form.voter_id} onChange={e=>f('voter_id',e.target.value)} placeholder="e.g. AP/12/34/567890"/>
            </div>

            <div className="form-group">
              <label className="form-label">UU ID</label>
              <input className="form-input" value={voter?.epic_id||''} readOnly style={{background:'#F5F5F5',color:'#9E9E9E'}}/>
              <div style={{fontSize:'0.68rem',color:'#9E6040',marginTop:2}}>Auto-generated — cannot be changed</div>
            </div>
          </div>

          {/* ── ADDRESS ── */}
          <div style={{background:'linear-gradient(135deg,#E8F5E9,#C8E6C9)',borderRadius:12,padding:'10px 14px',marginBottom:14}}>
            <div style={{fontWeight:700,fontSize:'0.78rem',color:'#1B5E20',marginBottom:10,textTransform:'uppercase',letterSpacing:'0.5px'}}>📍 Address</div>

            <div className="form-group">
              <label className="form-label">House Number</label>
              <input className="form-input" value={form.house_number} onChange={e=>f('house_number',e.target.value)} placeholder="House No."/>
            </div>

            <div className="form-group">
              <label className="form-label">Full Address</label>
              <input className="form-input" value={form.address} onChange={e=>f('address',e.target.value)} placeholder="Full address"/>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
              <div className="form-group">
                <label className="form-label">District</label>
                <input className="form-input" value={form.district} onChange={e=>f('district',e.target.value)} placeholder="District"/>
              </div>
              <div className="form-group">
                <label className="form-label">Area</label>
                <input className="form-input" value={form.area} onChange={e=>f('area',e.target.value)} placeholder="Area / Locality"/>
              </div>
            </div>
          </div>

          {/* ── VOTER STATUS ── */}
          <div className="form-group">
            <label className="form-label">Status</label>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {STATUSES.map(s=>(
                <label key={s} style={{display:'flex',alignItems:'center',gap:5,padding:'7px 14px',borderRadius:22,cursor:'pointer',background:form.status===s?'var(--saffron)':'white',color:form.status===s?'white':'#555',border:`2px solid ${form.status===s?'var(--saffron)':'rgba(255,107,0,0.2)'}`,fontWeight:600,fontSize:'0.83rem',userSelect:'none'}}>
                  <input type="radio" name="vstatus" value={s} checked={form.status===s} onChange={()=>f('status',s)} style={{display:'none'}}/>
                  {s==='Available'?'✅':s==='Shifted'?'🔄':s==='Expired'?'❌':'○'} {s}
                </label>
              ))}
            </div>
          </div>

          {/* ── ADDITIONAL ── */}
          <div style={{background:'linear-gradient(135deg,#F3E5F5,#E1BEE7)',borderRadius:12,padding:'10px 14px',marginBottom:14}}>
            <div style={{fontWeight:700,fontSize:'0.78rem',color:'#4A148C',marginBottom:10,textTransform:'uppercase',letterSpacing:'0.5px'}}>📋 Additional Info</div>

            <div className="form-group">
              <label className="form-label">Community</label>
              <input className="form-input" placeholder="Community / Caste" value={form.community} onChange={e=>f('community',e.target.value)}/>
            </div>

            <div className="form-group">
              <label className="form-label">Profession</label>
              <input className="form-input" placeholder="Enter profession" value={form.profession} onChange={e=>f('profession',e.target.value)} list="prof-dl"/>
              <datalist id="prof-dl">{['Private Employee','Government Employee','Business','IT Professional','Teacher','Doctor','Housewife','Student','Farmer','Retired','Advocate','Engineer'].map(p=><option key={p} value={p}/>)}</datalist>
            </div>

            <div className="form-group">
              <label className="form-label">Education</label>
              <select className="form-select" value={form.degree} onChange={e=>f('degree',e.target.value)}>
                <option value="">-- Select --</option>
                {['Below 10th','10th Pass','12th Pass','Diploma','Graduate','Post Graduate','PhD','Other'].map(d=><option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Mother Tongue</label>
              <select className="form-select" value={form.mother_tongue} onChange={e=>f('mother_tongue',e.target.value)}>
                <option value="">-- Select --</option>
                {MOTHER_TONGUES.map(m=><option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Reference / Volunteer</label>
              <input className="form-input" placeholder="Volunteer name" value={form.reference_name} onChange={e=>f('reference_name',e.target.value)}/>
            </div>
          </div>

          {/* ── GPS LOCATION ── */}
          <div className="form-group">
            <label className="form-label">📍 GPS Location</label>
            {form.latitude && form.longitude
              ? <div>
                  <div style={{background:'#E8F5E9',padding:'8px 12px',borderRadius:8,fontSize:'0.8rem',color:'#2E7D32',marginBottom:8,fontWeight:600}}>✅ Lat: {parseFloat(form.latitude).toFixed(6)}, Long: {parseFloat(form.longitude).toFixed(6)}</div>
                  <div style={{borderRadius:12,overflow:'hidden',border:'2px solid rgba(255,107,0,0.2)',marginBottom:8}}>
                    <iframe title="Location" width="100%" height="200" frameBorder="0" style={{display:'block'}}
                      src={`https://maps.google.com/maps?q=${form.latitude},${form.longitude}&z=16&output=embed`} allowFullScreen/>
                  </div>
                </div>
              : <div style={{background:'#FFF9C4',padding:'8px 12px',borderRadius:8,fontSize:'0.78rem',color:'#F57F17',marginBottom:8}}>No GPS saved</div>}

            {form.status === 'Shifted'
              ? <div>
                  <div style={{background:'#FFF3E0',padding:'8px 12px',borderRadius:8,fontSize:'0.78rem',color:'#E55100',marginBottom:8,fontWeight:600}}>
                    🔄 Status is Shifted — you can update the new location
                  </div>
                  <button className="btn btn-outline btn-full" onClick={getLocation} disabled={gettingLoc}>
                    {gettingLoc ? 'Getting GPS...' : '📍 Update New Location'}
                  </button>
                </div>
              : <div style={{background:'#F5F5F5',padding:'8px 12px',borderRadius:8,fontSize:'0.75rem',color:'#9E9E9E',marginBottom:4}}>
                  🔒 Location locked. Select <strong>Shifted</strong> status to update location.
                </div>
            }
          </div>

          {/* ── BUTTONS ── */}
          <div style={{display:'flex',gap:10,marginTop:10}}>
            <button className="btn btn-outline" style={{flex:1}} onClick={handleReset}>↺ Reset</button>
            <button className="btn btn-primary" style={{flex:2}} onClick={handleUpdateClick} disabled={saving}>
              {saving?<><span className="spinner" style={{width:14,height:14,borderWidth:2}}/> Updating...</>:'💾 Update'}
            </button>
            <button onClick={handleCall} style={{width:46,height:46,borderRadius:'50%',background:'#FFF3E0',border:'2px solid #FFCC80',cursor:'pointer',fontSize:'1.2rem',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>📞</button>
          </div>

          <div className="action-bar" style={{marginTop:16, gridTemplateColumns:'repeat(5,1fr)'}}>
            {isAdmin && <button className="action-btn action-btn-whatsapp" onClick={handleWA}><span style={{fontSize:'1.4rem'}}>💬</span><span>WhatsApp</span></button>}
            {isAdmin && <button className="action-btn action-btn-sms"      onClick={handleSMS}><span style={{fontSize:'1.4rem'}}>✉️</span><span>SMS</span></button>}
            <button className="action-btn action-btn-print"    onClick={handlePrint}><span style={{fontSize:'1.4rem'}}>🖨️</span><span>Print</span></button>
            <button className="action-btn" style={{background:'linear-gradient(135deg,#EDE7F6,#D1C4E9)',color:'#4A148C'}} onClick={handlePDFDownload}><span style={{fontSize:'1.4rem'}}>📄</span><span>PDF</span></button>
            <button className="action-btn action-btn-family"   onClick={fetchFamily}><span style={{fontSize:'1.4rem'}}>👨‍👩‍👧</span><span>Family</span></button>
          </div>
        </div>
      </div>

      {showFamily&&(
        <div className="modal-overlay" onClick={()=>setShowFamily(false)}>
          <div className="modal-content" onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <div className="section-title" style={{marginBottom:0}}>👨‍👩‍👧 Family Members</div>
              <button onClick={()=>setShowFamily(false)} style={{background:'none',border:'none',fontSize:'1.3rem',cursor:'pointer',color:'#757575'}}>✕</button>
            </div>
            {family.length===0
              ? <div className="empty-state" style={{padding:24}}><p>No family members found</p></div>
              : family.map(m=>(
                <div key={m.id} className="voter-list-item" onClick={()=>{setShowFamily(false);navigate(`/voter/${m.id}`);}}>
                  <div className={`voter-avatar ${m.gender==='F'?'avatar-f':'avatar-m'}`}>{m.name?.charAt(0)}</div>
                  <div>
                    <div style={{fontWeight:700}}>{m.name}</div>
                    <div style={{fontSize:'0.78rem',color:'#757575'}}>{m.gender==='M'?'Male':'Female'} · {m.age} yrs{m.relation_name?` · ${m.relation_name}`:''}</div>
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      )}
      {/* ── CONFIRMATION POPUP ── */}
      {showConfirm && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={()=>setShowConfirm(false)}>
          <div style={{background:'white',borderRadius:20,padding:'28px 24px',maxWidth:340,width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.2)'}} onClick={e=>e.stopPropagation()}>
            {/* Icon */}
            <div style={{width:56,height:56,borderRadius:'50%',background:'linear-gradient(135deg,#FFF3E0,#FFE0B2)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.8rem',margin:'0 auto 16px'}}>💾</div>

            {/* Title */}
            <div style={{fontFamily:"'Baloo 2',sans-serif",fontWeight:800,fontSize:'1.05rem',color:'#1A0A00',textAlign:'center',marginBottom:8}}>
              Confirm Update
            </div>

            {/* Message */}
            <div style={{fontSize:'0.85rem',color:'#5C3010',textAlign:'center',lineHeight:1.6,marginBottom:20}}>
              Are you sure you want to update the details for <strong>{voter?.name}</strong>?
            </div>

            {/* Changes summary */}
            <div style={{background:'#FFF8F0',borderRadius:12,padding:'10px 14px',marginBottom:20,fontSize:'0.78rem',color:'#9E6040'}}>
              <div style={{fontWeight:700,marginBottom:6,color:'#BF360C'}}>Changes to be saved:</div>
              {form.status && <div>📌 Status: <strong>{form.status}</strong></div>}
              {form.mobile && <div>📱 Mobile: <strong>••••{form.mobile.slice(-4)}</strong></div>}
              {form.community && <div>👥 Community: <strong>{form.community}</strong></div>}
              {form.profession && <div>💼 Profession: <strong>{form.profession}</strong></div>}
              {form.status === 'Shifted' && form.latitude && <div>📍 New Location updated</div>}
            </div>

            {/* Buttons */}
            <div style={{display:'flex',gap:10}}>
              <button onClick={()=>setShowConfirm(false)} style={{flex:1,padding:'12px',border:'2px solid rgba(255,107,0,0.25)',borderRadius:12,background:'white',color:'#E55100',fontWeight:700,fontSize:'0.88rem',cursor:'pointer',fontFamily:"'Outfit',sans-serif"}}>
                Cancel
              </button>
              <button onClick={handleUpdate} style={{flex:2,padding:'12px',border:'none',borderRadius:12,background:'linear-gradient(135deg,#E55100,#FF6B00)',color:'white',fontWeight:700,fontSize:'0.88rem',cursor:'pointer',fontFamily:"'Outfit',sans-serif",boxShadow:'0 4px 14px rgba(255,107,0,0.4)'}}>
                ✅ Yes, Update
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}